import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useTRPC } from "@/integrations/trpc/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	SolicitationHeader,
	SolicitationKeyDates,
	SolicitationDocuments,
	SolicitationMilestones,
	SolicitationContactInfo,
	SolicitationSubmitBidCta,
} from "@/components/solicitation";
import { SolicitationQA } from "@/components/solicitation/SolicitationQA";
import { getEffectiveStatus } from "@/lib/utils";

export const Route = createFileRoute("/public/$slug/solicitations/$solicitationId")({
	component: PublicSolicitationDetailPage,
});

function PublicSolicitationDetailPage() {
	const { slug, solicitationId } = Route.useParams();
	const navigate = useNavigate();
	const trpc = useTRPC();

	// Check if user is a vendor (not just authenticated)
	const { data: vendorStatus, isLoading: vendorLoading } = useQuery(
		trpc.vendorAuth.isVendor.queryOptions()
	);

	// Redirect authenticated VENDORS to the private vendor portal version
	// Don't redirect buyers or other non-vendor users
	useEffect(() => {
		if (!vendorLoading && vendorStatus?.isVendor) {
			navigate({
				to: "/vendor/$buyerSlug/solicitations/$solicitationId",
				params: { buyerSlug: slug, solicitationId },
				replace: true,
			});
		}
	}, [vendorLoading, vendorStatus?.isVendor, navigate, slug, solicitationId]);

	// Fetch buyer data
	const {
		data: buyer,
		isLoading: buyerLoading,
		error: buyerError,
	} = useQuery(trpc.public.organizations.getBySlug.queryOptions({ slug }));

	// Fetch solicitation details
	const {
		data: solicitation,
		isLoading: solicitationLoading,
		error: solicitationError,
	} = useQuery(
		trpc.public.solicitations.get.queryOptions({
			solicitationId,
		}),
	);

	// Fetch attachments (including archived)
	const { data: attachmentsData } = useQuery(
		trpc.public.solicitationDetails.attachments.queryOptions({
			solicitationId,
		}),
	);
	const attachments = attachmentsData?.attachments ?? [];
	const archivedAttachments = attachmentsData?.archivedAttachments ?? [];

	// Fetch forms (including archived) with PDF download URLs
	const { data: formsData } = useQuery(
		trpc.public.solicitationDetails.forms.queryOptions({
			solicitationId,
		}),
	);
	const forms = formsData?.forms ?? [];
	const archivedForms = formsData?.archivedForms ?? [];

	// Fetch milestones
	const { data: milestones } = useQuery(
		trpc.public.solicitationDetails.milestones.queryOptions({
			solicitationId,
		}),
	);

	// Show loading while checking vendor status or if vendor will be redirected
	if (vendorLoading || vendorStatus?.isVendor) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	// Handle loading state
	if (buyerLoading || solicitationLoading) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	// Handle buyer not found
	if (buyerError || !buyer) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="text-center">
					<h1 className="text-2xl font-bold mb-2">Organization Not Found</h1>
					<p className="text-muted-foreground">
						The organization you're looking for doesn't exist.
					</p>
				</div>
			</div>
		);
	}

	if (solicitationError || !solicitation) {
		return (
			<div className="min-h-screen bg-gray-50">
				<header className="bg-white border-b">
					<div className="max-w-4xl mx-auto px-4 py-4">
						<Button variant="ghost" asChild>
							<Link to="/public/$slug" params={{ slug }}>
								<ArrowLeft className="mr-2 h-4 w-4" />
								Back to Solicitations
							</Link>
						</Button>
					</div>
				</header>
				<main className="max-w-4xl mx-auto px-4 py-12 text-center">
					<h2 className="text-2xl font-bold">Solicitation not found</h2>
					<p className="text-muted-foreground mt-2">
						This solicitation is not available for public viewing.
					</p>
					<Button asChild className="mt-4">
						<Link to="/public/$slug" params={{ slug }}>
							View All Solicitations
						</Link>
					</Button>
				</main>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header */}
			<header className="bg-white border-b">
				<div className="max-w-4xl mx-auto px-4 py-4">
					<Button variant="ghost" asChild>
						<Link to="/public/$slug" params={{ slug }}>
							<ArrowLeft className="mr-2 h-4 w-4" />
							Back to Solicitations
						</Link>
					</Button>
				</div>
			</header>

			{/* Content */}
			<main className="max-w-4xl mx-auto px-4 py-8">
				{/* Header */}
				<SolicitationHeader
					title={solicitation.title ?? "Untitled Solicitation"}
					description={solicitation.description}
					type={solicitation.type}
					status={getEffectiveStatus(solicitation.status, solicitation.publish_date, solicitation.due_date)}
				/>

				{/* Key Dates */}
				<SolicitationKeyDates
					dueDate={solicitation.due_date}
					publishDate={solicitation.publish_date}
					documentCount={attachments.length}
				/>

				{/* Main content grid */}
				<div className="grid gap-6 md:grid-cols-3">
					<div className="md:col-span-2 space-y-6">
						{/* Documents & Forms */}
						<SolicitationDocuments
							attachments={attachments}
							archivedAttachments={archivedAttachments}
							forms={forms}
							archivedForms={archivedForms}
						/>

						{/* Milestones */}
						<SolicitationMilestones milestones={milestones ?? []} />

						{/* Q&A */}
						<SolicitationQA
							solicitationId={solicitationId}
							buyerOrgId={buyer.id}
							mode="public"
						/>
					</div>

					{/* Sidebar */}
					<div className="space-y-6">
						{/* Contact Info */}
						<SolicitationContactInfo
							name={buyer.name ?? ""}
							email={buyer.email ?? null}
							phone={buyer.phone ?? null}
							address={buyer.address ?? null}
						/>

						{/* Submit Bid CTA */}
						<SolicitationSubmitBidCta
							solicitationId={solicitation.id}
							buyerSlug={slug}
							status={getEffectiveStatus(solicitation.status, solicitation.publish_date, solicitation.due_date)}
							isAuthenticated={false}
						/>

						{/* Organization Info */}
						<Card>
							<CardHeader>
								<CardTitle>{buyer.name}</CardTitle>
							</CardHeader>
							<CardContent className="space-y-2 text-sm">
								{buyer.email && (
									<p>
										<span className="text-muted-foreground">Email:</span>{" "}
										<a
											href={`mailto:${buyer.email}`}
											className="text-primary hover:underline"
										>
											{buyer.email}
										</a>
									</p>
								)}
								{buyer.phone && (
									<p>
										<span className="text-muted-foreground">Phone:</span>{" "}
										{buyer.phone}
									</p>
								)}
								{buyer.address && (
									<p>
										<span className="text-muted-foreground">Address:</span>{" "}
										{buyer.address}
									</p>
								)}
							</CardContent>
						</Card>
					</div>
				</div>
			</main>

			{/* Footer */}
			<footer className="bg-white border-t mt-12">
				<div className="max-w-4xl mx-auto px-4 py-8 text-center text-sm text-muted-foreground">
					Powered by Bidscope - Government Procurement Platform
				</div>
			</footer>
		</div>
	);
}
